import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { splitCurrencySymbol } from "@/utils/format-money";

/**
 * The widget's badge: a small pill whose whole palette is derived from ONE colour token.
 *
 * Pass the colour (`color="urgent"`) and the badge builds the rest in CSS (`.badge-tinted`,
 * index.css): the background is a gradient from the token to a lighter, more saturated,
 * hue-shifted tint of it, a hairline border in the token, and the label is the token pushed
 * toward its contrast hue, much darker and very saturated — so a partner retint of the token
 * recolours the badge as a whole, and every badge in the widget shares the same relationship
 * between surface and type.
 *
 * Five variants: `solid` (the gradient derived from the token), `ghost` (the same gradient and
 * hairline derived from the surface the badge sits on, the token only on label and mark), `tonal`
 * (the token merged into the surface — a tinted chip that still belongs to the card) and `text`
 * (no surface: the token on the glyphs alone, with a faint ramp and a soft shadow).
 *
 * A leading mark slot, sized to the type, takes either an `icon` (any node — an SVG fills the
 * box) or, with `currencyMark`, the currency symbol of a money label ("€2,500" → "€" in the
 * slot, "2,500" as the label). Codes ("SEK 500") are not a glyph and stay in the label. With
 * no label at all the badge is the mark alone, a round chip (`size="lg"` for the task icons).
 *
 * Colours: `urgent` (the pays-before ceiling), `primary` (points, min. bet, task icons),
 * `prizes`, `active` (progress) and `locked` (grey). Add a token to the `color` variant to give
 * another surface a badge; the derivation is the same for every colour.
 */
const badgeVariants = cva(
  "inline-flex shrink-0 items-center rounded-full font-heading font-extrabold tabular-nums whitespace-nowrap [--badge-mark:1em]",
  {
    variants: {
      /** `solid`: the gradient surface derived from the token. `ghost`: the same construction
       *  derived from the card surface (`--badge-surface`), the token only on label and mark —
       *  the quiet sibling for secondary chips. */
      variant: {
        solid: "badge-tinted",
        ghost: "badge-ghost",
        /** The token merged into the card surface — between solid and ghost. */
        tonal: "badge-tonal",
        /** No surface at all: mark and label in the token, the label on a faint ramp and a soft
         *  shadow in the token's own shade. Keeps the badge's rhythm without its box. */
        text: "badge-text",
        /** The colour as the surface, the label in the page background: the text colour turned
         *  inside out. A compact, solid token for a figure that must lead its row. */
        inverted: "badge-inverted",
      },
      /** The token everything is derived from. Sets `--badge-color`; the CSS does the rest. */
      color: {
        urgent: "[--badge-color:var(--urgent)]",
        primary: "[--badge-color:var(--primary)]",
        prizes: "[--badge-color:var(--prizes)]",
        /** Progress (the `--active` token): a rung reached, a round running. */
        active: "[--badge-color:var(--active)]",
        /** Locked / not yet: the muted grey, so the badge reads as switched off. */
        locked: "[--badge-color:var(--muted-foreground)]",
        /** No accent: the secondary text colour — a ghost chip that is furniture, not a signal. */
        neutral: "[--badge-color:var(--foreground-secondary)]",
        /** The text colour itself: for figures that should read as body text with the badge's
         *  finish (the leaderboard scores). */
        foreground: "[--badge-color:var(--foreground)]",
        /** The podium metals (gold is `prizes`): places 2 and 3 on a prize table. */
        silver: "[--badge-color:var(--medal-silver)]",
        bronze: "[--badge-color:var(--medal-bronze)]",
      },
      /** `bubble`: a cartoon speech bubble, the pill with a tail at its bottom-left pointing
       *  down at what it speaks for (the launcher's "↑ N" over your cell). The tail is the
       *  badge's own token, so every variant and colour carries it. */
      shape: {
        pill: "",
        bubble: "badge-bubble",
      },
      size: {
        /** The quietest chip: 20px tall at both breakpoints, type at the 11px floor, a 12px
         *  mark — secondary chrome that must stay out of the way (the launcher's switch mark). */
        xs: "gap-1 px-1.5 py-0 text-[11px] leading-[18px] [--badge-mark:12px]",
        /** A count tucked under a prize on the launcher: 14px tall, type at the 11px floor, a
         *  10px mark — a chip that overlaps the edge of the plate it describes. */
        "2xs":
          "gap-0.5 px-1.5 py-0 text-[11px] leading-[14px] [--badge-mark:10px]",
        sm: "gap-1 px-2 py-0.5 text-[11px] leading-4 lg:px-2.5 lg:py-1 lg:text-[12px]",
        /** A row-sized badge with room for a mark and a label (the task rows): 14px type, the
         *  mark a step larger than the type so the glyph keeps its weight. */
        md: "gap-1.5 px-2.5 py-1 text-[13px] leading-5 [--badge-mark:16px] lg:gap-2 lg:px-3 lg:py-1.5 lg:text-[14px] lg:[--badge-mark:18px]",
        /** The task-icon chip: a 36px round mark, glyph at 18px. */
        lg: "gap-1.5 p-2 text-[16px] leading-none lg:p-[8px] lg:text-[18px]",
        /** No size of its own: the surrounding type's size and leading, no padding — a figure
         *  inside a row that fits its own text (the launcher's score, `variant="text"`). */
        inherit: "gap-1 p-0",
      },
    },
    defaultVariants: {
      variant: "solid",
      color: "urgent",
      shape: "pill",
      size: "sm",
    },
  }
);

export interface BadgeProps
  extends Omit<HTMLAttributes<HTMLSpanElement>, "color">,
    VariantProps<typeof badgeVariants> {
  /** The leading mark: an icon node, sized to the type (an SVG fills the slot). */
  icon?: ReactNode;
  /** A trailing mark after the label, outside its truncation (a task map's pips). */
  trailing?: ReactNode;
  /** Money label: move a leading currency symbol into the mark slot. Ignored when `icon` is set. */
  currencyMark?: boolean;
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      variant,
      color,
      shape,
      size,
      icon,
      trailing,
      currencyMark = false,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const cash =
      !icon && currencyMark && typeof children === "string"
        ? splitCurrencySymbol(children)
        : null;
    // No glyph for a word currency ("12,500 kr"): the label stands alone.
    const mark = icon ?? cash?.symbol ?? null;
    const label = cash ? cash.rest : children;
    const markOnly = mark !== null && label == null;
    // A currency symbol is a glyph, not an icon box: no fixed slot, no added space ("€50"), and it
    // always leads — the widget's one convention, whatever the locale.
    const markNode = mark !== null && (
      <span
        aria-hidden
        className={cn(
          // `upright`: a leaf the in-game button turns back upright on its
          // mirrored side (`igw-button.css`); no effect anywhere else.
          "upright inline-flex shrink-0 items-center justify-center [&>svg]:size-full",
          cash ? "h-(--badge-mark)" : "size-(--badge-mark)"
        )}
      >
        {mark}
      </span>
    );
    return (
      <span
        ref={ref}
        className={cn(
          badgeVariants({ variant, color, shape, size }),
          // The mark alone: a round chip, as wide as it is tall, the mark centred.
          // `lg:p-0` too: the size variants restore their padding at `lg`, which would squeeze
          // the mark out of a fixed-size chip on desktop.
          markOnly &&
            cn(
              "justify-center p-0 lg:p-0",
              (size ?? "sm") === "lg"
                ? "size-9"
                : size === "xs"
                  ? "size-5"
                  : "size-[26px]"
            ),
          cash && "gap-0",
          className
        )}
        aria-label={cash ? String(children) : props["aria-label"]}
        {...props}
      >
        {markNode}
        {label != null && (
          <span className="upright min-w-0 truncate">{label}</span>
        )}
        {trailing}
      </span>
    );
  }
);
Badge.displayName = "Badge";
